import type { ChatStorePrivateState } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { ChatStoreAction } from './types';

const INITIAL_PROMPT_HISTORY_LIMIT = 100;
const REFRESH_PROMPT_HISTORY_LIMIT = 25;

/** Loads 100 once per scope; an explicitly invalidated snapshot can merge 25 recent items. */
export const ensurePromptHistoryLoaded: (scope: string) => ChatStoreAction<Promise<void>> =
	(scope) =>
	async ({ getState, setState }, { assistanceService, sendAnalyticsEvent }) => {
		const history = getState().promptHistory;
		const cached = history?.scope === scope ? history : undefined;
		// Claim this open synchronously: pending requests and failures must not retry on rerender.
		if (cached && !cached.needsRefresh) {
			return;
		}

		const pending: NonNullable<ChatStorePrivateState['promptHistory']> = {
			scope,
			needsRefresh: false,
			hasLoaded: cached?.hasLoaded ?? false,
			requestToken: {},
			items: cached?.items ?? [],
			...(cached?.removedSessionIds ? { removedSessionIds: cached.removedSessionIds } : {}),
		};
		setState({ promptHistory: pending });

		if (!assistanceService.getPromptHistory) {
			// Unsupported hosts have a successful empty cache, not an error or retry loop.
			setState({ promptHistory: { ...pending, hasLoaded: true } });
			return;
		}

		try {
			const { items } = await assistanceService.getPromptHistory({
				limit: pending.hasLoaded ? REFRESH_PROMPT_HISTORY_LIMIT : INITIAL_PROMPT_HISTORY_LIMIT,
			});
			const current = getState().promptHistory;
			if (current?.requestToken !== pending.requestToken) {
				return;
			}

			// New records win by message ID. Keep older items and establish ordering here, once.
			const itemsByMessageId = new Map(
				[...current.items, ...items].map((item) => [item.messageId, item]),
			);
			setState({
				promptHistory: {
					...current,
					items: [...itemsByMessageId.values()]
						.filter(({ sessionId }) => !current.removedSessionIds?.includes(sessionId))
						.sort((a, b) => b.createdAt - a.createdAt),
					hasLoaded: true,
				},
			});
		} catch (error) {
			// Retain cached and locally added items. A reset or explicit invalidation enables a retry.
			if (
				getState().promptHistory?.requestToken !== pending.requestToken ||
				(error instanceof Error && error.name === 'AbortError')
			) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'promptHistory',
				attributes: { loadType: pending.hasLoaded ? 'refresh' : 'initial' },
			});
		}
	};
